<!-- eslint-disable vue/max-attributes-per-line -->
<template>
  <div class="flex flex-col gap-y-10 p-8">
    <header class="flex items-center justify-between w-full">
      <p class="text-primary-A900 font-bold text-4xl italic">
        {{ props.jobDetails.title }}
      </p>
    </header>
    <ul class="flex items-center gap-x-12 w-full">
      <li>
        <p class="text-sm text-secondary-700">
          {{ $t('vacancyCity') }}
        </p>
        <p class="text-sm text-primary-A900 font-medium">
          {{ props.jobDetails.location }}
        </p>
      </li>
      <li>
        <p class="text-sm text-secondary-700">
          {{ $t('vacancyType') }}
        </p>
        <p class="text-sm text-primary-A900 font-medium">
          {{ props.jobDetails.job_type }}
        </p>
      </li>
      <li>
        <p class="text-sm text-secondary-700">
          {{ $t('vacancyYears') }}
        </p>
        <p class="text-sm text-primary-A900 font-medium">
          {{ props.jobDetails.years_of_experience }}
        </p>
      </li>
      <li>
        <p class="text-sm text-secondary-700">
          {{ $t('vacancyDate') }}
        </p>
        <p class="text-sm text-primary-A900 font-medium">
          {{ props.jobDetails.created_at }}
        </p>
      </li>
    </ul>
    <div>
      <p class="text-sm text-secondary-700">
        {{ $t('vacancyReqs') }}
      </p>
      <p class="text-sm text-primary-A900 font-medium">
        {{ props.jobDetails.requirements }}
      </p>
    </div>
    <AppButton
      v-if="!isApplying"
      class="flex items-center justify-center w-full text-lg mt-12 px-3 py-3 relative z-1 [ font-bold md:w-1/2 ]"
      variant="default"
      @click="isApplying = !isApplying"
    >
      {{ $t('applyNow') }}
    </AppButton>
    <form v-if="isApplying" id="apply-to-vacancy" @submit="submit">
      <fieldset>
        <div class="mt-4 flex flex-col gap-y-2">
          <TextInput
            id="full-name"
            name="fullName"
            class="w-full text-base"
            :label="$t('fullName')"
            border
            rounded
            required
          />

          <TextInput id="email" name="email" class="w-full text-base" :label="$t('email')" border rounded required />

          <TextInput
            id="phoneNumber"
            name="phoneNumber"
            type="tel"
            class="w-full text-base"
            :label="$t('phoneNumber')"
            border
            rounded
            required
          />

          <FileInput id="cv" name="cv" accept=".pdf,.doc,.docx" :label="$t('cv')" :inner-text="$t('uploadCv')" />

          <AppButton
            class="flex items-center justify-center w-full text-lg mt-12 px-3 py-3 relative z-1 [ font-bold md:w-1/2 ]"
            variant="default"
            :loading="isSubmitting"
          >
            {{ $t('submit') }}
          </AppButton>
        </div>
      </fieldset>
    </form>
  </div>
</template>
<script setup lang="ts">
import * as yup from 'yup';
import { useMutation } from 'villus';
import { SubmitCareerVacancyFormDocument, type JobVacanciesOutput } from '~/graphql/Vacancies';

const props = defineProps({
  jobDetails: {
    type: Object as PropType<JobVacanciesOutput>,
    default: () => ({
      title: '',
    }),
  },
});

const { t } = useI18n({
  useScope: 'global',
});
const { t: $t } = useI18n({
  useScope: 'local',
});
const isApplying = ref(false);
const showAlerts = inject('showAlerts');
const { execute } = useMutation(SubmitCareerVacancyFormDocument);
const { handleSubmit, isSubmitting, resetForm } = useForm({
  validationSchema: toTypedSchema(
    yup.object({
      fullName: yup.string().required(t('validation.required')),
      email: yup.string().email().required(t('validation.required')),
      phoneNumber: yup
        .string()
        .required(t('validation.required'))
        .min(11, t('validation.minLength', { length: 11 })),
      cv: yup
        .mixed<File>()
        .test('required', t('validation.required'), value => {
          return value && value.size > 0;
        })
        .test('fileSize', 'File too large', value => {
          return value && value.size <= 5000000;
        })
        .test('fileType', 'Unsupported File Format', value => {
          return (
            value &&
            [
              'application/pdf',
              'application/msword',
              'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
            ].includes(value.type)
          );
        }),
    }),
  ),
});

const submit = handleSubmit(async value => {
  try {
    const data = await execute({
      input: {
        name: value.fullName,
        email: value.email,
        phone_number: value.phoneNumber,
        attachment: value.cv ? await extractBase64WithoutHeader(value.cv) : '',
        vacancyId: props.jobDetails.id,
      },
    });
    if (data.error?.message) {
      error(data.error?.message);
    } else {
      showAlerts({ type: 'success' });
      resetForm();
    }
  } catch (e) {
    showAlerts({ type: 'error' });
    console.error(e);
  }
});
</script>
<i18n>
    {
        "en": {
            "vacancyCity": "City",
            "vacancyType": "Location Type",
            "vacancyDate": "Posted On",
            "vacancyYears": "Years of Experience",
            "vacancyReqs": "Job Requirements",
            "yearOfExp": "Year",
            "yearsOfExp": "Years",
            "fullName": "Full Name",
            "email": "Email",
            "phoneNumber": "Phone Number",
            "cv": "CV",
            "uploadCv": "Upload CV",
            "formSubmitted": "Your form has been submitted successfully",
            "formSubmissionError": "An error occurred while submitting the form",
            "submit": "Submit",
            "applyNow": "Apply Now",
            "success": {
            "title": "Message Sent!",
            "subtitle": "We will contact you shortly"
            },
            "error": {
                "title": "Failed",
                "subtitle": "Sorry something went wrong while sending the message, try again later"
            }
        },
        "ar": {
            "vacancyCity": "المدينه",
            "vacancyType": "التنقل",
            "vacancyDate": "تاريخ النشر",
            "vacancyYears": "سنوات الخبرة",
            "vacancyReqs": "متطلبات الوظيفة",
            "yearOfExp": "سنة",
            "yearsOfExp": "سنوات",
            "formSubmitted": "تم تقديم النموذج بنجاح",
            "formSubmissionError": "حدث خطأ أثناء تقديم النموذج",
            "success": {
            "title": "تم الارسال بنجاح",
            "subtitle": "سيتم التواصل معك في وقت لاحق"
            },
            "error": {
                "title": "فشل",
                "subtitle": "حدث خطأ أثناء الارسال، حاول مرة أخرى"
            },
            "fullName": "الاسم الكامل",
            "email": "البريد الإلكتروني",
            "phoneNumber": "رقم الهاتف",
            "cv": "السيرة الذاتية",
            "submit": "تقديم",
            "applyNow": "قدم الآن",
            }
    }
</i18n>
